import { format, isSameMonth } from "date-fns";
import { Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { formatDateTime, titleCase } from "../../lib/format";
import { Card, CardHeader, DataTable, EmptyState, StatusPill } from "../../ui/components";
import type { CalendarEntry, CalendarMode } from "./calendarTypes";
import {
  entryDateKey,
  entryMarkers,
  entryPath,
  formatCalendarTime,
  localDateKey,
} from "./calendarView";

interface CalendarViewsProps {
  mode: CalendarMode;
  anchor: Date;
  days: Date[];
  items: CalendarEntry[];
  // What this half of the diary holds. The same grid serves the client's own programme and the
  // hires taken against the same rooms, so the word for a row comes from the caller rather than
  // being assumed: a calendar that called an internal festival a booking is how the event side
  // ended up sounding like somebody else's business.
  noun: string;
  // Where the create affordances point, or empty when this person may not create on this side.
  createPath: string;
  // Rescheduling moves a hire, so it stays gated on lettings authority whichever side is shown.
  canManageBookings: boolean;
}

function entriesOnDay(items: CalendarEntry[], day: Date): CalendarEntry[] {
  const key = localDateKey(day);
  return items
    .filter((entry) => entryDateKey(entry) === key)
    .sort((left, right) => left.start.localeCompare(right.start));
}

function EventButton({ entry, compact = false }: { entry: CalendarEntry; compact?: boolean }) {
  const navigate = useNavigate();
  const markers = entryMarkers(entry);
  return (
    <button
      className={`${compact ? "calendar-event" : "week-event"} ${entry.conflictOverridden ? "conflict" : ""}`}
      type="button"
      onClick={() => navigate(entryPath(entry))}
      title={`${entry.title}, ${formatDateTime(entry.start, entry.timezone)}, ${entry.locationNames.join(", ")}`}
    >
      <strong>
        {formatCalendarTime(entry.start, entry.timezone)} {entry.title}
      </strong>
      <small>{entry.locationNames.join(", ") || "No location assigned"}</small>
      {markers.length ? <span className="calendar-markers">{markers.join(" · ")}</span> : null}
    </button>
  );
}

function MonthView({ anchor, days, items, noun, createPath }: Omit<CalendarViewsProps, "mode">) {
  return (
    <Card className="calendar-card calendar-print-area">
      <section
        className="table-scroll"
        aria-label={`${format(anchor, "MMMM yyyy")} ${noun} calendar`}
      >
        <div className="calendar-grid operational-calendar-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((weekday) => (
            <div className="calendar-weekday" key={weekday}>
              {weekday}
            </div>
          ))}
          {days.map((day) => (
            <div
              className={`calendar-day ${isSameMonth(day, anchor) ? "" : "outside"}`}
              key={day.toISOString()}
            >
              <div className="calendar-day-heading">
                <span className="calendar-date">{format(day, "d")}</span>
                {createPath ? (
                  <Link
                    className="calendar-add no-print"
                    aria-label={`Create ${noun} on ${format(day, "MMMM d")}`}
                    to={`${createPath}?date=${format(day, "yyyy-MM-dd")}`}
                  >
                    <Plus size={14} />
                  </Link>
                ) : null}
              </div>
              {entriesOnDay(items, day).map((entry) => (
                <EventButton compact entry={entry} key={entry.occurrenceId} />
              ))}
            </div>
          ))}
        </div>
      </section>
    </Card>
  );
}

function WeekView({ days, items, noun }: Pick<CalendarViewsProps, "days" | "items" | "noun">) {
  return (
    <Card className="calendar-card calendar-print-area">
      <section className="calendar-week-board" aria-label={`Weekly ${noun} calendar`}>
        {days.map((day) => {
          const dayEntries = entriesOnDay(items, day);
          return (
            <section className="calendar-week-column" key={day.toISOString()}>
              <header>
                <strong>{format(day, "EEE")}</strong>
                <span>{format(day, "MMM d")}</span>
              </header>
              {dayEntries.length ? (
                dayEntries.map((entry) => <EventButton entry={entry} key={entry.occurrenceId} />)
              ) : (
                <span className="calendar-empty-day">No {noun}s</span>
              )}
            </section>
          );
        })}
      </section>
    </Card>
  );
}

function DayView({
  anchor,
  items,
  noun,
  createPath,
}: Pick<CalendarViewsProps, "anchor" | "items" | "noun" | "createPath">) {
  const dayEntries = entriesOnDay(items, anchor);
  return (
    <Card className="calendar-print-area">
      <CardHeader
        title="Day schedule"
        description={`${dayEntries.length} ${noun}${dayEntries.length === 1 ? "" : "s"} on ${format(anchor, "MMMM d")}. Times include each venue’s timezone.`}
        actions={
          createPath ? (
            <Link
              className="button secondary no-print"
              to={`${createPath}?date=${format(anchor, "yyyy-MM-dd")}`}
            >
              <Plus size={16} /> Add {noun}
            </Link>
          ) : undefined
        }
      />
      {dayEntries.length ? (
        <ol className="day-schedule">
          {dayEntries.map((entry) => (
            <li key={entry.occurrenceId}>
              <span className="day-schedule-time">
                {formatCalendarTime(entry.start, entry.timezone)}
                <small>to {formatCalendarTime(entry.end, entry.timezone)}</small>
              </span>
              <EventButton entry={entry} />
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState
          title={`No ${noun}s on this day`}
          description={`Choose another day or create a ${noun}.`}
        />
      )}
    </Card>
  );
}

function PortfolioView({ days, items, noun }: Pick<CalendarViewsProps, "days" | "items" | "noun">) {
  // Rows are the scoped venue's spaces. This view compared venues before single-venue
  // scoping; the whole page now reads through one venue, so the comparison that still
  // means something is between the rooms inside it.
  const spaceNames = Array.from(
    new Set(
      items.flatMap((entry) => (entry.locationNames.length ? entry.locationNames : ["Unassigned"])),
    ),
  ).sort();
  return (
    <Card className="calendar-card calendar-print-area">
      <CardHeader
        title="Space schedule"
        description={`Compare every space in this venue across the week. A ${noun} using more than one space appears in each relevant row.`}
      />
      {spaceNames.length ? (
        <div className="table-scroll">
          <table className="portfolio-calendar">
            <caption className="sr-only">Every {noun} grouped by space and day</caption>
            <thead>
              <tr>
                <th scope="col">Space</th>
                {days.map((day) => (
                  <th scope="col" key={day.toISOString()}>
                    {format(day, "EEE")}
                    <small>{format(day, "MMM d")}</small>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {spaceNames.map((spaceName) => (
                <tr key={spaceName}>
                  <th scope="row">{spaceName}</th>
                  {days.map((day) => {
                    const dayItems = entriesOnDay(items, day).filter((entry) =>
                      spaceName === "Unassigned"
                        ? entry.locationNames.length === 0
                        : entry.locationNames.includes(spaceName),
                    );
                    return (
                      <td key={day.toISOString()}>
                        {dayItems.length ? (
                          dayItems.map((entry) => (
                            <EventButton compact entry={entry} key={entry.occurrenceId} />
                          ))
                        ) : (
                          <span className="calendar-empty-day">None</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title={`No ${noun}s this week`}
          description="Change the week or clear a filter."
        />
      )}
    </Card>
  );
}

function AgendaView({
  items,
  noun,
  canManageBookings,
}: Pick<CalendarViewsProps, "items" | "noun" | "canManageBookings">) {
  return (
    <Card className="calendar-print-area">
      <CardHeader
        title="Agenda"
        description={`${items.length} occurrence${items.length === 1 ? "" : "s"} in this view.`}
      />
      {items.length ? (
        <DataTable
          caption={`Every ${noun} in the current calendar view`}
          items={items}
          getKey={(entry) => entry.occurrenceId}
          columns={[
            {
              label: "When",
              render: (entry) => (
                <>
                  <strong>{formatDateTime(entry.start, entry.timezone)}</strong>
                  <br />
                  <small>to {formatDateTime(entry.end, entry.timezone)}</small>
                </>
              ),
            },
            {
              label: "What",
              render: (entry) => (
                <>
                  <strong>{entry.title}</strong>
                  <br />
                  <small>
                    {entry.reference} · {entry.customerName}
                  </small>
                </>
              ),
            },
            {
              label: "Space",
              render: (entry) => <>{entry.locationNames.join(", ") || "No location assigned"}</>,
            },
            {
              label: "Status",
              render: (entry) => (
                <>
                  <StatusPill value={entry.status} />
                  <br />
                  <small>{titleCase(entry.paymentState)}</small>
                </>
              ),
            },
            {
              label: "Attention",
              render: (entry) =>
                entryMarkers(entry).length ? entryMarkers(entry).join(" · ") : "None",
            },
            {
              label: "Actions",
              render: (entry) => (
                <div className="inline no-print">
                  <Link className="button ghost small" to={entryPath(entry)}>
                    Open
                  </Link>
                  {canManageBookings && entry.kind === "booking" ? (
                    <Link
                      className="button secondary small"
                      to={`/calendar/bookings/${entry.bookingId}/reschedule`}
                    >
                      Reschedule
                    </Link>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      ) : (
        <EmptyState
          title={`No ${noun}s in view`}
          description={`Adjust the filters or create a ${noun} in this period.`}
        />
      )}
    </Card>
  );
}

export function CalendarViews(props: CalendarViewsProps) {
  if (props.mode === "month") return <MonthView {...props} />;
  if (props.mode === "week")
    return <WeekView days={props.days} items={props.items} noun={props.noun} />;
  if (props.mode === "day")
    return (
      <DayView
        anchor={props.anchor}
        items={props.items}
        noun={props.noun}
        createPath={props.createPath}
      />
    );
  if (props.mode === "portfolio")
    return <PortfolioView days={props.days} items={props.items} noun={props.noun} />;
  return (
    <AgendaView items={props.items} noun={props.noun} canManageBookings={props.canManageBookings} />
  );
}
